package com.sweetlime.bingeamigo.presentation.features.auth

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.sweetlime.bingeamigo.model.UserProfile
import com.sweetlime.bingeamigo.repository.AuthRepository
import com.sweetlime.bingeamigo.repository.UserRepository
import com.sweetlime.bingeamigo.util.UsernameGenerator
import com.sweetlime.bingeamigo.state.AuthState
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

class AuthViewModel(
    private val repository: AuthRepository,
    private val userRepository: UserRepository
) : ViewModel() {

    private val _state =
        MutableStateFlow<AuthState>(
            AuthState.Idle
        )

    val state = _state.asStateFlow()

    fun login(
        email: String,
        password: String
    ) {
        viewModelScope.launch {
            _state.value = AuthState.Loading
            try {
                repository.login(email, password)

                if (!repository.isEmailVerified()) {
                    runCatching { repository.sendEmailVerification() }
                    repository.logout()
                    _state.value = AuthState.Error(
                        "Please verify your email before logging in. A new verification link has been sent to $email."
                    )
                    return@launch
                }

                val uid = repository.currentUserId()!!

                if (userRepository.getUserProfile(uid) == null) {
                    userRepository.createUserProfile(
                        UserProfile(id = uid, username = UsernameGenerator.generate(), avatarUrl = "", xp = 0, level = 1)
                    )
                }

                _state.value = AuthState.Success(uid)
            } catch (e: Exception) {
                _state.value = AuthState.Error(mapAuthError(e, isLogin = true))
            }
        }
    }

    fun signup(
        email: String,
        password: String
    ) {
        viewModelScope.launch {
            _state.value = AuthState.Loading
            try {
                repository.signup(email, password)
                val uid = repository.currentUserId()!!

                try {
                    userRepository.createUserProfile(
                        UserProfile(id = uid, username = UsernameGenerator.generate(), avatarUrl = "", xp = 0, level = 1)
                    )
                } catch (profileError: Exception) {
                    runCatching { repository.deleteAccount() }
                    _state.value = AuthState.Error("Account setup failed. Please try again.")
                    return@launch
                }

                repository.sendEmailVerification()
                _state.value = AuthState.PendingVerification(email)
            } catch (e: Exception) {
                _state.value = AuthState.Error(mapAuthError(e, isLogin = false))
            }
        }
    }

    fun sendPasswordReset(email: String) {
        viewModelScope.launch {
            _state.value = AuthState.Loading
            try {
                repository.sendPasswordResetEmail(email)
                _state.value = AuthState.PasswordResetSent(email)
            } catch (e: Exception) {
                _state.value = AuthState.Error(mapPasswordResetError(e))
            }
        }
    }

    fun resetState() {
        _state.value = AuthState.Idle
    }

    private fun mapPasswordResetError(e: Exception): String {
        val msg = e.message?.lowercase() ?: ""
        return when {
            "badly formatted" in msg || "invalid email" in msg ->
                "Please enter a valid email address."
            "no user record" in msg || "user not found" in msg ->
                "No account found with this email."
            "network" in msg || "timeout" in msg || "unreachable" in msg ->
                "Network error. Please check your connection."
            "too many requests" in msg ->
                "Too many attempts. Please try again later."
            else -> "Failed to send reset email. Please try again."
        }
    }

    private fun mapAuthError(e: Exception, isLogin: Boolean): String {
        val msg = e.message?.lowercase() ?: ""
        return when {
            "badly formatted" in msg || "invalid email" in msg ->
                "Please enter a valid email address."
            "supplied auth credential" in msg || "wrong password" in msg ||
            "password is invalid" in msg || "invalid login credentials" in msg ||
            "invalid_credentials" in msg ->
                "Incorrect email or password."
            "no user record" in msg || "user not found" in msg ->
                "No account found with this email."
            "email address is already in use" in msg || "email already in use" in msg ->
                "An account with this email already exists."
            "password should be at least" in msg || "weak-password" in msg ||
            "password must be" in msg ->
                "Password must be at least 6 characters."
            "network" in msg || "timeout" in msg || "unreachable" in msg ->
                "Network error. Please check your connection."
            "too many requests" in msg || "unusual activity" in msg ->
                "Too many attempts. Please try again later."
            "user account has been disabled" in msg || "account has been disabled" in msg ->
                "This account has been disabled."
            else -> if (isLogin) "Login failed. Please try again." else "Sign up failed. Please try again."
        }
    }

    fun logout() {
        _state.value = AuthState.Idle
        viewModelScope.launch {
            runCatching { repository.logout() }
        }
    }
}